import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useId, useState } from "react";
import { ApiError } from "../lib/api";
import {
  EXPORT_CSV_PATH,
  EXPORT_JSON_PATH,
  fetchSettings,
  MIN_BIRTH_DATE,
  queryKeys,
  updateSettings,
} from "./api";
import { AppPage } from "./app-nav";
import { ConcertsPaste } from "./concerts-paste";
import { todayIso } from "./dates";
import { DeleteAccount } from "./delete-account";
import { SessionGate } from "./session-gate";
import { TracksSettings } from "./tracks-settings";

/**
 * Les reglages (`/reglages`, planche « Reglages ») : la date de naissance, qui sert a lire « vers
 * 15 ans » ou « en 4e », les pistes (#588, `tracks-settings.tsx`), le collage des concerts (ticket 6, `concerts-paste.tsx`), et « Mes
 * données » : les deux exports et la suppression de la frise et du compte (ticket 8).
 */
export function SettingsPage() {
  const settings = useQuery({ queryKey: queryKeys.settings, queryFn: fetchSettings, retry: false });
  if (settings.isPending) {
    return (
      <p className="text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  }
  if (settings.isError) {
    return <SessionGate error={settings.error} retry={() => void settings.refetch()} />;
  }
  return (
    <AppPage>
      <h1 className="tl-display m-0 text-[28px]">Réglages</h1>
      <BirthDateForm initial={settings.data.birthDate} />
      <TracksSettings />
      <ConcertsPaste />
      <MyData />
    </AppPage>
  );
}

function BirthDateForm({ initial }: { initial: string | null }) {
  const queryClient = useQueryClient();
  const id = useId();
  const [value, setValue] = useState(initial ?? "");
  const [saved, setSaved] = useState(false);
  useEffect(() => setValue(initial ?? ""), [initial]);

  const save = useMutation({
    mutationFn: () => updateSettings(value === "" ? null : value),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.settings, data);
      // Les apercus de dates en age ou en classe dependent de la date de naissance.
      void queryClient.invalidateQueries({ queryKey: ["t", "parse"] });
      setSaved(true);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    save.mutate();
  };

  return (
    <form className="flex max-w-md flex-col gap-2" onSubmit={submit}>
      <h2 className="m-0 text-[15px] font-semibold" id={`${id}-titre`}>
        Ta date de naissance
      </h2>
      <input
        aria-describedby={`${id}-aide`}
        aria-labelledby={`${id}-titre`}
        className="min-h-11 rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground"
        max={todayIso()}
        min={MIN_BIRTH_DATE}
        onChange={(event) => {
          setValue(event.target.value);
          setSaved(false);
        }}
        type="date"
        value={value}
      />
      <p className="m-0 text-xs text-muted-foreground" id={`${id}-aide`}>
        Elle sert à comprendre « vers 15 ans » ou « en 4e ». Personne d'autre ne la voit.
      </p>
      <button
        className="min-h-11 self-start rounded-[10px] bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        disabled={save.isPending}
        type="submit"
      >
        {save.isPending ? "Enregistrement..." : "Enregistrer"}
      </button>
      <div aria-live="polite">
        {saved ? <p className="m-0 text-sm text-tl-ok">C'est noté.</p> : null}
        {save.isError ? (
          <p className="m-0 text-sm text-destructive" role="alert">
            {save.error instanceof ApiError
              ? save.error.message
              : "L'enregistrement n'a pas abouti. Réessaie."}
          </p>
        ) : null}
      </div>
    </form>
  );
}

/** Le mot a taper pour effacer sa frise : on ne la retrouve pas. */
export const DELETE_WORD = "supprimer";

const exportLink =
  "inline-flex min-h-11 items-center justify-center rounded-[10px] border border-input px-3 text-sm text-foreground no-underline";

/** « Mes données » (planche « Reglages ») : tout exporter, les points en CSV, tout effacer. */
function MyData() {
  return (
    <section aria-labelledby="mes-donnees" className="flex max-w-md flex-col gap-2">
      <h2 className="m-0 text-[15px] font-semibold" id="mes-donnees">
        Mes données
      </h2>
      <div className="grid grid-cols-2 gap-2">
        {/* Le serveur repond en piece jointe datee : le navigateur enregistre le fichier. */}
        <a className={exportLink} download href={EXPORT_JSON_PATH}>
          Tout exporter (JSON)
        </a>
        <a className={exportLink} download href={EXPORT_CSV_PATH}>
          Points (CSV)
        </a>
      </div>
      <p className="m-0 text-xs text-muted-foreground">
        Le JSON garde tout (points, pistes, proches, propositions) ; le CSV s'ouvre dans un tableur.
      </p>
      <DeleteAccount
        buttonLabel="Supprimer ma frise et mon compte"
        confirmLabel="Tout supprimer"
        confirmWord={DELETE_WORD}
        doneMessage="C'est fait : ta frise et ton compte sont effacés."
        explanation="Tous tes moments, tes pistes, tes proches et leurs propositions seront effacés, puis ton compte. Tes proches n'auront plus accès à rien. On ne pourra rien récupérer : exporte ta frise avant si tu veux la garder."
        heading="Supprimer ta frise et ton compte ?"
      />
    </section>
  );
}
